import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useAuth } from "../../../auth/store";
import { ErrorState } from "../../../components/States";
import { AutoLoadMore, DataTable, PageHeader } from "../../../components/ui";
import { backendApi, backendKeys, type LogLine, type LogPattern } from "../api";
import { BackendActions, BackendTabs, ConnectPrompt, DebouncedSearch, LevelBadge, ServiceSelect, replayLink, useBackendRange, useFacets, useFilters, when } from "../components";

const KEYS = ["q", "service", "level", "templateId", "view"] as const;

const LEVELS = [
  { value: "9", label: "Info and above" },
  { value: "13", label: "Warnings and above" },
  { value: "17", label: "Errors and above" },
  { value: "21", label: "Fatal only" },
];

export function LogsPage() {
  const [range] = useBackendRange();
  const [f, setF, clear] = useFilters(KEYS);
  const facets = useFacets(range);
  const patterns = f.view === "patterns";
  const filtered = !!(f.q || f.service || f.level || f.templateId);

  return (
    <>
      <PageHeader title="Backend" actions={<BackendActions />}>
        <BackendTabs />
        <div className="ins-filters" role="search" aria-label="Log filters">
          {!patterns && <DebouncedSearch value={f.q} onChange={(q) => setF({ q })} placeholder="Message text or trace ID…" label="Search logs" />}
          <ServiceSelect value={f.service} onChange={(service) => setF({ service })} services={facets.data?.services ?? []} />
          <label>
            Level
            <select value={f.level ?? ""} onChange={(e) => setF({ level: e.target.value || undefined })} autoComplete="off">
              <option value="">Any level</option>
              {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </label>
          <label>
            Show
            <select value={f.view ?? ""} onChange={(e) => setF({ view: e.target.value || undefined, templateId: undefined })} autoComplete="off">
              <option value="">Every line</option>
              <option value="patterns">Grouped by pattern</option>
            </select>
          </label>
          {filtered && <button type="button" className="btn ghost" onClick={() => setF({ q: undefined, service: undefined, level: undefined, templateId: undefined })}>Clear</button>}
        </div>
      </PageHeader>
      {patterns ? <Patterns service={f.service} level={f.level} onOpen={(templateId) => setF({ view: undefined, templateId })} /> : <Lines q={f.q} service={f.service} level={f.level} templateId={f.templateId} filtered={filtered} clear={clear} noServices={facets.isSuccess && facets.data.services.length === 0} />}
    </>
  );
}

function Lines({ q: text, service, level, templateId, filtered, clear, noServices }: { q?: string; service?: string; level?: string; templateId?: string; filtered: boolean; clear: () => void; noServices: boolean }) {
  const auth = useAuth();
  const [range] = useBackendRange();
  const query = { range, q: text, service, minSeverity: level ? Number(level) : undefined, templateId };
  const q = useInfiniteQuery({
    queryKey: backendKeys.logs(auth.currentProjectId, query),
    queryFn: ({ pageParam }) => backendApi.logs({ ...query, cursor: pageParam, limit: 100 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!auth.currentProjectId,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  if (q.isError) return <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>;
  if (!q.isPending && !filtered && rows.length === 0 && noServices) {
    return <ConnectPrompt title="No backend logs yet" hint="Connect a service and its logs show up here, each one linked to the request and replay it came from." />;
  }
  return (
    <div className="card table-scroll stack-table be-table be-logs">
      {templateId && (
        <div className="be-banner small">
          Showing one log pattern. <button type="button" className="btn ghost small" onClick={clear}>Show every line</button>
        </div>
      )}
      <DataTable<LogLine>
        ariaLabel="Backend logs"
        rows={rows}
        rowKey={(l) => `${l.ts}-${l.spanId ?? ""}-${l.body.slice(0, 40)}`}
        loading={q.isPending}
        loadingMore={q.isFetchingNextPage}
        empty={filtered ? <>No log lines match these filters. <button type="button" className="btn ghost small" onClick={clear}>Clear filters</button></> : "No log lines in this time range."}
        columns={[
          { key: "at", header: "Time", label: "Time", width: 170, render: (l) => <span className="small mono">{when(l.ts)}</span> },
          { key: "lvl", header: "Level", label: "Level", width: 90, render: (l) => <LevelBadge severity={l.severityNumber} /> },
          { key: "svc", header: "Service", label: "Service", width: 140, render: (l) => <span className="small">{l.service}</span> },
          {
            key: "msg",
            header: "Message",
            className: "be-msg",
            render: (l) => (
              <span className="cell-stack">
                <span className="be-log-body">{l.body}</span>
                {l.exceptionStack && (
                  <details className="be-stack">
                    <summary>{l.exceptionType || "Stack"}</summary>
                    <pre>{l.exceptionStack}</pre>
                  </details>
                )}
              </span>
            ),
          },
          {
            key: "links",
            header: "Linked to",
            label: "Linked to",
            width: 170,
            render: (l) => (
              <span className="be-links small">
                {l.traceId ? <Link to={"/backend/traces/$traceId" as never} params={{ traceId: l.traceId } as never}>Request</Link> : null}
                {l.sessionId ? <Link {...replayLink(l.sessionId, l.ts)}>Replay</Link> : null}
                {!l.traceId && !l.sessionId ? <span className="muted">None</span> : null}
              </span>
            ),
          },
        ]}
      />
      <AutoLoadMore hasMore={!!q.hasNextPage} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more log lines" />
    </div>
  );
}

function Patterns({ service, level, onOpen }: { service?: string; level?: string; onOpen: (templateId: string) => void }) {
  const auth = useAuth();
  const [range] = useBackendRange();
  const query = { range, service, minSeverity: level ? Number(level) : undefined };
  const q = useQuery({ queryKey: backendKeys.patterns(auth.currentProjectId, query), queryFn: () => backendApi.patterns(query), enabled: !!auth.currentProjectId });
  if (q.isError) return <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>;
  return (
    <div className="card table-scroll stack-table be-table">
      <DataTable<LogPattern>
        ariaLabel="Log patterns"
        rows={q.data?.items ?? []}
        rowKey={(p) => `${p.service}-${p.templateId}`}
        loading={q.isPending}
        onRowClick={(p) => onOpen(p.templateId)}
        empty="No log patterns in this time range."
        columns={[
          {
            key: "tpl",
            header: "Pattern",
            render: (p) => (
              <span className="cell-stack">
                <span className="be-log-body mono">{p.template}</span>
                <small>{p.service}{p.new ? <> · <span className="badge info">New</span></> : null}</small>
              </span>
            ),
          },
          { key: "lvl", header: "Highest level", label: "Highest level", width: 120, render: (p) => <LevelBadge severity={p.maxSeverity} /> },
          { key: "n", header: "Lines", label: "Lines", width: 100, align: "right", render: (p) => <span className="mono">{p.lines.toLocaleString()}</span> },
          { key: "last", header: "Last seen", label: "Last seen", width: 170, render: (p) => <span className="small">{when(p.lastSeen)}</span> },
        ]}
      />
    </div>
  );
}
